import React from "react";
import Grid from "components/Grid";
import Modal from "components/Modal";
import Joi from "joi";
import Swal from "sweetalert2";
import { useValidator } from "react-joi";
import { useDispatch, useSelector } from "react-redux";
import InputCustom from "components/InputCustom";
import { useTranslation } from "react-i18next";
import {
  getCategoriesProduct,
  addOrUpdateCategoriesProduct,
  deleteCategoriesProduct,
} from "actions/Catalogs";
import { notify } from "utils";
import { parseISO, format } from "date-fns";
import { filterDate } from "components/DateCatalogs/filterDate";

const CategoriesProduct = ({ view }) => {
  const dispatch = useDispatch();
  const { currentProject } = useSelector((state) => state.auth);
  const { categoriesProduct } = useSelector((state) => state.catalog);

  const [openModal, setOpenModal] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [id, setId] = React.useState();
  const [isLoad, setIsLoad] = React.useState(false);
  const [t] = useTranslation("catalog");
  const [tg] = useTranslation("global");

  const { permissions } = view;

  const { state, setData, setExplicitField, validate } = useValidator({
    initialData: {
      name: null,
      description: null,
    },
    schema: Joi.object({
      name: Joi.string()
        .min(2)
        .max(50)
        .required()
        .messages({
          "string.empty": t("error.empty"),
          "string.base": t("error.type", { type: "string" }),
          "string.min": t("error.min"),
          "string.max": t("error.max", { quantity: "50" }),
          "any.required": t("error.required"),
        }),
      description: Joi.string()
        .min(2)
        .max(50)
        .required()
        .messages({
          "string.empty": t("error.empty"),
          "string.base": t("error.type", { type: "string" }),
          "string.min": t("error.min"),
          "string.max": t("error.max", { quantity: "50" }),
          "any.required": t("error.required"),
        }),
    }),
    explicitCheck: {
      name: false,
      description: false,
    },
    validationOptions: {
      abortEarly: true,
    },
  });

  const buttonRef = React.useRef();

  React.useEffect(() => {
    if (currentProject && currentProject?.id) {
      dispatch(getCategoriesProduct(currentProject.id));
    }
  }, [dispatch, currentProject]);

  const handleOnSubmit = async () => {
    const { name, description } = state.$data;

    if (state.$all_errors.length > 0 || state.$all_source_errors.length > 0) {
      setShowError(true);
      validate();
      notify("error", t("error.general_toast"), 1200);
      return;
    }

    setIsLoad(true);
    if (buttonRef & buttonRef?.current) buttonRef.current.disabled = true;
    let idProject = currentProject.id;
    let isSuccess = await dispatch(
      addOrUpdateCategoriesProduct({
        name,
        description,
        idProject,
        id,
      })
    );
    if (buttonRef & buttonRef?.current) buttonRef.current.disabled = false;
    setIsLoad(false);
    setShowError(false);
    if (isSuccess) {
      dispatch(getCategoriesProduct(idProject));
      setData((old) => ({
        ...old,
        name: null,
        description: null,
      }));
      setId(null);
      setOpenModal(false);
    }
  };

  const handleDelete = async (idCategoriesProduct) => {
    Swal.fire({
      title: tg("button.title"),
      text: tg("button.text"),
      icon: "error",
      confirmButtonText: tg("button.confirm"),
      cancelButtonText: tg("button.cancel"),
      cancelButtonColor: "#000000",
      confirmButtonColor: "#FF3447",
      showCancelButton: true,
    }).then(async (result) => {
      if (result.isConfirmed && idCategoriesProduct) {
        dispatch(
          deleteCategoriesProduct(idCategoriesProduct, currentProject.id)
        );
      }
    });
  };

  return (
    <>
      <Grid
        title={t("categoriesProduct.title")}
        columns={[
          {
            Header: "Id",
            accessor: "id",
            filter: "text",
            width: '10%'
          },
          {
            Header: t("categoriesProduct.name"),
            accessor: "name",
            filter: "text",
            width: '30%'
          },
          {
            Header: t("categoriesProduct.description"),
            accessor: "description",
            filter: "text",
            width: '30%'
          },
          {
            Header: t("categoriesProduct.updated_at"),
            accessor: "updatedAt",
            filter: filterDate,
            width: '20%',
            Cell: ({ value }) => {
              if (value) return format(parseISO(value), "dd/MM/yyyy HH:mm");
              return value;
            },
          },
        ]}
        data={categoriesProduct || []}
        handleAdd={() => {
          if (permissions.write) {
            setOpenModal(true);
          }
        }}
        handleEdit={(row) => {
          const { name, description, id } = row;
          setId(id);
          setData((old) => ({
            ...old,
            name,
            description,
          }));
          setOpenModal(true);
        }}
        handleDelete={({ id }) => handleDelete(id)}
        write={permissions.write}
      />
      {openModal ? (
        <Modal
          openModal={openModal}
          closeModal={() => {
            setData((old) => ({
              ...old,
              name: null,
              description: null,
            }));
            setShowError(false);
            setOpenModal(false);
            setId(null);
          }}
          titleModal="Categorias Productos"
          onSubmit={handleOnSubmit}
          disableAdd={state.$invalid}
          iconEdit={id > 0 ? true : false}
          buttonRef={buttonRef}
          processFetch={isLoad}
        >
          <h4 className="font-[Gravity] font-bold tex-[18px] my-2">
            {t("categoriesProduct.info_modal")}
          </h4>
          <div className="flex flex-wrap ">
            <InputCustom
              type={"text"}
              labelText={t("categoriesProduct.name")}
              errorMessage={
                showError && state.$errors.name.length > 0
                  ? state.$errors.name[0].$message
                  : null
              }
              onBlur={() => setExplicitField("name", true)}
              onChange={(e) => {
                e.persist();
                setData((old) => ({
                  ...old,
                  name: e.target.value,
                }));
              }}
              value={state.$data.name}
            />
            <InputCustom
              type={"text"}
              labelText={t("categoriesProduct.description")}
              errorMessage={
                showError && state.$errors.description.length > 0
                  ? state.$errors.description[0].$message
                  : null
              }
              onBlur={() => setExplicitField("description", true)}
              onChange={(e) => {
                e.persist();
                setData((old) => ({
                  ...old,
                  description: e.target.value,
                }));
              }}
              value={state.$data.description}
            />
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default CategoriesProduct;
